import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";

export interface FeePlan {
  id: string;
  tenant_id: string;
  branch_id: string | null;
  name: string;
  description: string | null;
  vertical: string;
  plan_type: string;
  base_amount: number;
  duration_hours: number | null;
  duration_tier: string | null;
  billing_cycle: string;
  due_day_of_month: number;
  auto_generate_invoice: boolean;
  allow_partial_payment: boolean;
  tax_inclusive: boolean;
  includes_facilities: string[];
  shift_ids: string[];
  payment_methods: string[];
  emi_enabled: boolean;
  emi_installments: number;
  is_active: boolean;
  valid_from: string | null;
  valid_until: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface FeePlanListResponse {
  items: FeePlan[];
  total: number;
}

export interface CreateFeePlanBody {
  name: string;
  description?: string | null;
  plan_type: string;
  base_amount: number;
  duration_hours?: number | null;
  duration_tier?: string | null;
  billing_cycle?: string;
  due_day_of_month?: number;
  payment_methods?: string[];
}

const FEES_HEADERS = { "X-Vertical": "study_centre" };

export function formatRupee(amount: number): string {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(amount);
}

export function useFeePlans(params?: {
  plan_type?: string;
  is_active?: boolean;
  limit?: number;
  offset?: number;
}) {
  const qs = new URLSearchParams();
  if (params?.plan_type) qs.set("plan_type", params.plan_type);
  if (params?.is_active !== undefined) qs.set("is_active", String(params.is_active));
  if (params?.limit) qs.set("limit", String(params.limit));
  if (params?.offset) qs.set("offset", String(params.offset));
  const suffix = qs.toString() ? `?${qs.toString()}` : "";

  return useQuery<FeePlanListResponse>({
    queryKey: ["fee-plans", params],
    queryFn: () =>
      api.get<FeePlanListResponse>(`/api/finance/fee-plans${suffix}`, {
        headers: FEES_HEADERS,
      }),
  });
}

export function useCreateFeePlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateFeePlanBody) =>
      api.post<FeePlan>("/api/finance/fee-plans", body, { headers: FEES_HEADERS }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["fee-plans"] });
      void qc.invalidateQueries({ queryKey: ["dashboard-summary"] });
    },
  });
}

export function useDeleteFeePlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.delete<{ ok: boolean }>(`/api/finance/fee-plans/${id}`, {
        headers: FEES_HEADERS,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["fee-plans"] });
      void qc.invalidateQueries({ queryKey: ["dashboard-summary"] });
    },
  });
}
